import { useCallback, useEffect, useRef, useState } from "react";
import { resolveImg, refreshFileToken, variantWidth, downloadSigned } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { FileText, ImageOff, RotateCcw, X, Maximize2, Download } from "lucide-react";
import { usePdfBlob } from "@/components/shared/usePdfBlob";

const withNonce = (u, n) => (u ? `${u}${u.includes("?") ? "&" : "?"}_r=${n}` : u);

// Renders a protected file (image or PDF) served from /api/files with a signed
// token. Handles expired tokens (retry after refresh) and PDF previews.
// 216: `thumb` = největší velikost prvku v CSS px (dlaždice, miniatura) → načte
// se zmenšený náhled ze serveru místo originálu a líně (loading="lazy"); bez
// `thumb` originál hned (lightbox, doklady).
// 336: `frameClass` = výška čtečky PDF (v úzkém formuláři menší); s `thumb` je PDF jen ikona —
// dlaždice nemá místo na čtečku a iframe by z ní přetekl přes okolní obsah.
export function SignedImg({ url, alt = "", className = "", testid, thumb, frameClass = "h-[420px] md:h-[560px]" }) {
  const isPdf = (url || "").toLowerCase().includes(".pdf");
  const [nonce, setNonce] = useState(0);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [noVariant, setNoVariant] = useState(false);   // náhled selhal → zkusit originál
  const retriesRef = useRef(0);
  const [full, setFull] = useState(false);   // celoobrazovkový náhled PDF

  // force = true jen při ručním „Zkusit znovu" (vynutí nový podpis).
  const prime = useCallback(async (force = false) => {
    // 296: náhled na token nečeká donekonečna — po 8 s se načte s tím, co je (selhání pak řeší onError)
    await Promise.race([refreshFileToken(force), new Promise((r) => setTimeout(r, 8000))]);
    retriesRef.current = 0;
    setFailed(false);
    setNoVariant(false);
    setNonce(force ? Date.now() : 0);   // 0 = bez cache-busteru → URL je stabilní a cachovatelná
    setReady(true);
  }, []);

  useEffect(() => { setReady(false); prime(); }, [url, prime]);

  useEffect(() => {
    if (!full) return;
    const on = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [full]);

  const w = !isPdf && !noVariant ? variantWidth(thumb) : null;
  const onError = async () => {
    if (retriesRef.current >= 2) {
      // Poslední záchrana u náhledu: originál (kdyby server náhled vyrobit nezvládl).
      if (w) { retriesRef.current = 1; setNoVariant(true); setNonce(Date.now()); return; }
      setFailed(true); return;
    }
    retriesRef.current += 1;
    await refreshFileToken(true);
    setNonce(Date.now());
  };

  const base = resolveImg(url, w);
  const src = ready && base ? (nonce ? withNonce(base, nonce) : base) : null;
  const pdfSrc = usePdfBlob(isPdf && !thumb ? src : null);
  if (!url) return null;

  if (isPdf && thumb) {
    return (
      <div className={`grid place-items-center bg-secondary text-muted-foreground ${className}`} title={alt || "PDF"} data-testid={testid}>
        <FileText size={Math.min(28, Math.max(16, Math.round(thumb / 3)))} aria-hidden="true" />
      </div>
    );
  }

  if (isPdf) {
    const fileName = decodeURIComponent((url.split("?")[0].split("/").pop()) || "doklad.pdf");
    return (
      <div className={className} data-testid={testid}>
        {pdfSrc ? (
          <iframe src={pdfSrc} title={alt || "PDF"} data-testid={testid ? `${testid}-frame` : "signed-pdf-frame"}
            className={`block w-full ${frameClass} rounded-sm border border-border bg-white`} />
        ) : (
          <div className={`grid w-full ${frameClass} place-items-center rounded-sm border border-border bg-secondary text-muted-foreground`}>
            <FileText size={30} aria-hidden="true" />
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          <button type="button" onClick={() => setFull(true)}
            data-testid={testid ? `${testid}-fullscreen` : "signed-pdf-fullscreen"}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
            <Maximize2 size={15} /> Zvětšit na celou obrazovku
          </button>
          <button type="button" onClick={() => downloadSigned(url, fileName)} data-testid={testid ? `${testid}-download` : "signed-pdf-download"}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:underline">
            <Download size={15} /> Stáhnout
          </button>
        </div>

        {full && pdfSrc && (
          <div className="fixed inset-0 z-[70] bg-black/80 flex flex-col" data-testid="pdf-fullscreen">
            <div className="shrink-0 flex items-center justify-between gap-3 px-4 text-white"
              style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))", paddingBottom: "0.75rem" }}>
              <span className="text-sm font-bold truncate">{alt || "Doklad"}</span>
              <button type="button" onClick={() => setFull(false)} data-testid="pdf-fullscreen-close"
                className="shrink-0 inline-flex items-center gap-1.5 h-11 px-4 rounded-full bg-white/15 hover:bg-white/25 font-bold">
                <X size={18} /> Zavřít
              </button>
            </div>
            <iframe src={pdfSrc} title={alt || "PDF"} className="flex-1 w-full bg-white"
              style={{ paddingBottom: "env(safe-area-inset-bottom)" }} />
          </div>
        )}
      </div>
    );
  }

  if (failed) {
    return (
      <div className={`flex flex-col items-center justify-center gap-3 rounded-sm border border-border bg-muted/30 p-6 text-center ${className}`}
        data-testid={testid ? `${testid}-fallback` : "signed-img-fallback"}>
        <ImageOff size={28} className="text-muted-foreground" />
        <div className="text-sm text-muted-foreground">Náhled se nepodařilo načíst</div>
        <Button variant="outline" size="sm" onClick={() => prime(true)} data-testid={testid ? `${testid}-retry` : "signed-img-retry"}>
          <RotateCcw size={14} /> Zkusit znovu
        </Button>
      </div>
    );
  }

  // Náhledy (mřížky) líně: prohlížeč stáhne jen dlaždice v okně. Velké prohlížeče
  // (lightbox) bez `thumb` načítají hned — předem načtené sousední fotky ve skrytém
  // pásu by se s lazy nenačetly.
  const lazy = thumb ? { loading: "lazy", decoding: "async" } : {};
  return src ? <img src={src} alt={alt} className={className} data-testid={testid} onError={onError} {...lazy} /> : null;
}
